<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Flag Evaluation Volume
                            </h2>
                        </div>
                    </div>

                    <div
                        class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                    >
                        <div>
                            <label
                                for="evaluation-volume-filter-from"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                From
                            </label>
                            <input
                                id="evaluation-volume-filter-from"
                                type="date"
                                v-model="filters.startDate"
                                data-testid="evaluation-volume-filter-from"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            />
                        </div>
                        <div>
                            <label
                                for="evaluation-volume-filter-to"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                To
                            </label>
                            <input
                                id="evaluation-volume-filter-to"
                                type="date"
                                v-model="filters.endDate"
                                data-testid="evaluation-volume-filter-to"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            />
                        </div>
                        <div>
                            <label
                                for="evaluation-volume-filter-granularity"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Granularity
                            </label>
                            <select
                                id="evaluation-volume-filter-granularity"
                                v-model="filters.granularity"
                                data-testid="evaluation-volume-filter-granularity"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            >
                                <option value="day">Daily</option>
                                <option value="week">Weekly</option>
                                <option value="month">Monthly</option>
                            </select>
                        </div>
                        <div>
                            <label
                                for="evaluation-volume-filter-environment"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Environment
                            </label>
                            <select
                                id="evaluation-volume-filter-environment"
                                v-model="filters.environmentKey"
                                data-testid="evaluation-volume-filter-environment"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            >
                                <option value="">All environments</option>
                                <option
                                    v-for="environment in environments"
                                    :key="environment.key"
                                    :value="environment.key"
                                >
                                    {{ environment.name }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <zen-button
                                variant="secondary"
                                size="field"
                                data-testid="evaluation-volume-apply-filters"
                                @click="load"
                            >
                                Apply
                            </zen-button>
                        </div>
                    </div>

                    <zen-loading-indicator v-if="loading" />

                    <zen-message-block v-if="error" type="error">
                        {{
                            error.message ||
                            'Failed to load flag evaluation volume. Please try again.'
                        }}
                    </zen-message-block>

                    <template v-if="!loading && !error">
                        <div
                            class="rounded-md border border-taupe-200 bg-white p-4"
                            data-testid="evaluation-volume-chart"
                        >
                            <LineChart
                                v-if="chartData.datasets.length > 0"
                                :data="chartData"
                                :options="chartOptions"
                            />
                            <zen-empty-table-message
                                v-else
                                compact
                                icon="ZenIconChartBar"
                            >
                                No evaluations recorded for this date range.
                            </zen-empty-table-message>
                        </div>

                        <div
                            v-if="environmentTotals.length > 0"
                            class="flex flex-wrap gap-3"
                            data-testid="evaluation-volume-environment-totals"
                        >
                            <div
                                v-for="row in environmentTotals"
                                :key="row.environment_key"
                                class="rounded-md border border-taupe-200 bg-white px-3 py-2 text-sm"
                            >
                                <span class="text-taupe-500"
                                    >{{ row.environment_key }}:</span
                                >
                                <span class="font-medium text-taupe-800">{{
                                    row.total.toLocaleString()
                                }}</span>
                            </div>
                        </div>

                        <div
                            class="-mx-4 mt-2 overflow-hidden shadow-sm sm:-mx-6 md:mx-0 md:rounded-lg"
                        >
                            <table class="min-w-full divide-y divide-taupe-300">
                                <thead class="bg-taupe-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            class="w-full lg:w-3/4 py-3.5 pl-4 pr-3 text-left text-sm lg:text-base font-medium text-taupe-800 sm:pl-6"
                                        >
                                            Flag
                                        </th>
                                        <th
                                            scope="col"
                                            class="w-1/4 px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800"
                                        >
                                            Evaluations
                                        </th>
                                    </tr>
                                </thead>
                                <tbody
                                    class="divide-y divide-taupe-200 bg-white"
                                >
                                    <tr
                                        v-for="flag in topFlags"
                                        :key="flag.key"
                                    >
                                        <td
                                            class="w-full max-w-0 py-4 pl-4 pr-3 text-sm lg:text-base font-medium text-taupe-800 sm:w-auto sm:max-w-none sm:pl-6"
                                        >
                                            <div>
                                                {{ flag.name }}
                                            </div>
                                            <div
                                                class="font-light font-mono text-sm text-taupe-800"
                                            >
                                                <router-link
                                                    v-if="flag.key"
                                                    :to="{
                                                        name: 'ProjectFlagDetailScreen',
                                                        params: {
                                                            projectKey:
                                                                current.projectKey,
                                                            environmentKey:
                                                                current.environmentKey,
                                                            key: flag.key,
                                                        },
                                                    }"
                                                    class="flex space-x-1 text-taupe-500 hover:text-sky-500"
                                                >
                                                    <span>{{ flag.key }}</span>
                                                    <zen-icon-open-link
                                                        css-classes="h-4 w-4"
                                                        stroke-width="2"
                                                    />
                                                </router-link>
                                                <span v-else>—</span>
                                            </div>
                                        </td>
                                        <td
                                            class="px-3 py-4 text-sm lg:text-base text-taupe-800"
                                        >
                                            {{ flag.total.toLocaleString() }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>

                    <div v-if="!loading" class="text-sm pb-10">
                        Report Generated:
                        <zen-date-time :value="generatedAt" />
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import { Line as LineChart } from 'vue-chartjs'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js'

import ReportService from '@/services/report.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { useSessionStore } from '@/stores/session'
import { useCancelableLoad } from '@/composables/useCancelableLoad'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
)

// Fallback palette for environments without a configured color, cycled by index.
const FALLBACK_COLORS = ['#0ea5e9', '#f97316', '#22c55e', '#a855f7', '#ef4444']

export default {
    name: 'EvaluationVolume',
    components: { LineChart },
    setup() {
        const { current } = useCurrentEnvironment()
        const { loading, error, run } = useCancelableLoad()
        return {
            current,
            sessionStore: useSessionStore(),
            loading,
            error,
            run,
        }
    },
    data() {
        return {
            timeSeries: [],
            topFlags: [],
            environmentTotals: [],
            generatedAt: null,
            filters: {
                startDate: '',
                endDate: '',
                granularity: 'day',
                environmentKey: '',
            },
        }
    },
    computed: {
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        environments() {
            const project = (this.sessionStore.data?.projects?.data ?? []).find(
                (p) => p.key === this.current.projectKey
            )

            return project?.environments?.data ?? []
        },
        environmentKeys() {
            const keys = new Set()
            this.timeSeries.forEach((bucket) => {
                Object.keys(bucket.environments).forEach((key) => keys.add(key))
            })
            return Array.from(keys).sort()
        },
        chartData() {
            return {
                labels: this.timeSeries.map((bucket) => bucket.bucket),
                datasets: this.environmentKeys.map((key, index) => ({
                    label: key,
                    data: this.timeSeries.map((bucket) => {
                        const entry = Object.entries(bucket.environments).find(
                            ([environmentKey]) => environmentKey === key
                        )
                        return entry ? entry[1] : 0
                    }),
                    borderColor: this.colorForEnvironment(key, index),
                    backgroundColor: this.colorForEnvironment(key, index),
                    tension: 0.2,
                })),
            }
        },
        chartOptions() {
            return {
                responsive: true,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    y: { beginAtZero: true },
                },
            }
        },
    },
    watch: {
        environmentScopeKey() {
            this.load()
        },
    },
    methods: {
        colorForEnvironment(key, index) {
            const environment = this.environments.find((e) => e.key === key)
            return (
                environment?.color ??
                FALLBACK_COLORS[index % FALLBACK_COLORS.length]
            )
        },
        defaultDateRange() {
            const end = new Date()
            const start = new Date()
            start.setDate(start.getDate() - 29)

            return {
                startDate: start.toISOString().slice(0, 10),
                endDate: end.toISOString().slice(0, 10),
            }
        },
        load() {
            this.run(
                (signal) =>
                    ReportService.getEvaluationVolume(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            startDate: this.filters.startDate,
                            endDate: this.filters.endDate,
                            granularity: this.filters.granularity,
                            environmentKey:
                                this.filters.environmentKey || undefined,
                        },
                        { signal }
                    ),
                (response) => {
                    this.timeSeries = response.data.time_series
                    this.topFlags = response.data.top_flags
                    this.environmentTotals = response.data.environment_totals
                    this.generatedAt = response.data.generated_at
                },
                () => {
                    this.timeSeries = []
                    this.topFlags = []
                    this.environmentTotals = []
                }
            )
        },
    },
    mounted() {
        Object.assign(this.filters, this.defaultDateRange())
        this.load()
    },
}
</script>
